import { HORDE_GLOSSARY_A } from './horde-glossary-a';
import { HORDE_GLOSSARY_B } from './horde-glossary-b';

/**
 * Canonical map from Horde-Mode-specific jargon to its rules.json section (contracts §5's `RuleSection.id`).
 * Hand-curated per the repo's "start as a TS const, promote to JSON only if curation friction appears" convention
 * (see `shared-data-access-content`'s existing content-loading pattern for the promotion path if this ever needs
 * to grow past easy code review).
 *
 * Every `sectionId` below was verified against a direct parse of `apps/horde-web/public/content/rules.json` (75
 * entries: `intro`, `1`, `2`…`2.10`, `3`…`3.10`, `4`…`4.5a`, `5`…`5.12`, `6`, `7`…`7.5-<faction>`, `8`) - no
 * invented ids.
 *
 * Consumed by `KeywordRefComponent` (`libs/shared/ui`) and, via `resolveKeywordDefinition`, by any feature that
 * wants to know whether a keyword resolves before deciding how to render it (e.g. a datasheet keyword-chip loop
 * choosing between a linked chip and a plain badge).
 */
export interface HordeGlossaryTerm {
    /**
     * Canonical display term.
     */
    readonly term: string;
    /**
     * Alternate forms/casings actually observed in app copy that should resolve to this same entry.
     */
    readonly aliases: readonly string[];
    /**
     * Short, plain-language definition shown in the inline disclosure.
     */
    readonly definition: string;
    /**
     * `RuleSection.id` this term is defined/discussed in - always deep-linkable via `/library/rules#<id>`.
     */
    readonly sectionId: string;
}

/**
 * A resolved keyword: enough to render `KeywordRefComponent`'s inline disclosure, plus an optional "read the full
 * rule" deep link when a `rules.json` section exists for it (Horde-glossary terms always have one; base-40k terms,
 * per `base-40k-glossary.ts`, never do).
 */
export interface KeywordDefinition {
    readonly definition: string;
    readonly sectionId?: string;
}

export const HORDE_GLOSSARY: readonly HordeGlossaryTerm[] = [...HORDE_GLOSSARY_A, ...HORDE_GLOSSARY_B];

/**
 * Normalizes a keyword/alias for lookup: trims, lowercases, strips `[...]` bracket punctuation (weapon keywords
 * like `[HEAVY]` are both bracketed in rules prose and bare on datasheet chips), and collapses internal
 * whitespace.
 */
export function normalizeKeywordTerm(value: string): string {
    return value.trim().toLowerCase().replaceAll(/[[\]]/g, '').replaceAll(/\s+/g, ' ');
}

/**
 * Builds a term+alias → entry lookup index, keyed by {@link normalizeKeywordTerm}.
 */
export function buildKeywordIndex<T extends { term: string; aliases: readonly string[] }>(
    entries: readonly T[]
): ReadonlyMap<string, T> {
    const index = new Map<string, T>();

    for (const entry of entries) {
        index.set(normalizeKeywordTerm(entry.term), entry);

        for (const alias of entry.aliases) {
            index.set(normalizeKeywordTerm(alias), entry);
        }
    }

    return index;
}

/**
 * `term`/alias → {@link HordeGlossaryTerm} lookup for every Horde-Mode-specific glossary entry.
 */
export const KEYWORD_SECTION_MAP: ReadonlyMap<string, HordeGlossaryTerm> = buildKeywordIndex(HORDE_GLOSSARY);
